import React, { useState, useEffect } from "react";
import moment from "moment";
import logger from "../utils/logger";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../shadcn/ui/dialog";
import { Button } from "../shadcn/ui/button";
import { Input } from "../shadcn/ui/input";
import { Label } from "../shadcn/ui/label";
import { Textarea } from "../shadcn/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "../shadcn/ui/popover";
import { Calendar } from "../shadcn/ui/calendar";
import { ChevronDownIcon, Loader2 } from "lucide-react";

function EditAnnouncementModal({ isOpen, isClose, onSubmit, announcement }) {
  const [formData, setFormData] = useState({
    id: "",
    title: "",
    message: "",
    datetimeToSend: new Date(),
  });
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [selectedTime, setSelectedTime] = useState("10:00:00");

  useEffect(() => {
    if (announcement) {
      const announcementDate = announcement.datetimeToSend ? new Date(announcement.datetimeToSend) : new Date();
      setFormData({
        id: announcement.id,
        title: announcement.title || "",
        message: announcement.body || "",
        datetimeToSend: announcementDate,
      });

      // Set date and time picker states
      setSelectedDate(announcementDate);
      const timeString = announcementDate.toTimeString().split(" ")[0];
      setSelectedTime(timeString);
    }
  }, [announcement]);

  // Combine date and time when either changes
  useEffect(() => {
    if (selectedDate && selectedTime) {
      const [hours, minutes, seconds] = selectedTime.split(":");
      const combined = new Date(selectedDate);
      combined.setHours(hours, minutes, seconds);
      setFormData(prev => ({ ...prev, datetimeToSend: combined }));
    }
  }, [selectedDate, selectedTime]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    setLoading(true);

    return onSubmit({
      id: formData.id,
      title: formData.title,
      message: formData.message,
      datetimeToSend: moment(formData.datetimeToSend).format("YYYY-MM-DDTHH:mm:ss"),
    })
      .then(response => {
        if (response?.status === 200) {
          isClose();
        }
      })
      .catch(error => {
        logger.error("Error in submit:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Dialog open={isOpen} onOpenChange={isClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-center">Edit Announcement</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              type="text"
              name="title"
              placeholder="Title"
              value={formData.title}
              onChange={handleChange}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              name="message"
              rows={3}
              placeholder="Message"
              value={formData.message}
              onChange={handleChange}
            />
          </div>

          {/* Date and Time Selection */}
          <div className="flex gap-4">
            <div className="flex flex-col gap-3 flex-1">
              <Label htmlFor="date">Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" id="date" className="w-full justify-between font-normal">
                    {selectedDate ? selectedDate.toLocaleDateString() : "Select date"}
                    <ChevronDownIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={date => date < new Date()}
                    initialFocus
                    classNames={{
                      caption: "flex justify-center pt-1 relative items-center",
                      nav_button_previous: "absolute left-1",
                      nav_button_next: "absolute right-1",
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex flex-col gap-3 flex-1">
              <Label htmlFor="time">Time *</Label>
              <Input
                type="time"
                id="time"
                step="1"
                value={selectedTime}
                onChange={e => setSelectedTime(e.target.value)}
                className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
              />
            </div>
          </div>
        </form>

        <DialogFooter className="flex justify-center gap-4">
          <Button type="button" variant="outline" onClick={isClose} className="w-32">
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={loading} className="w-32">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditAnnouncementModal;
